'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import useSWR from 'swr'
import GoogleReviewSlider from './GoogleReviewSlider'

interface HomeReviewProps {
	heading?: {
		title?: string
	}
	content_collection?: {
		id?: string
		reviews_id?: {
			rating?: number
			author?: string
			comment?: string
		}
	}[]
	button?: {
		label?: string
		href?: string
	}
}

export const fields = [
	'heading.title',
	'content_collection.id',
	'content_collection.reviews_id.rating',
	'content_collection.reviews_id.author',
	'content_collection.reviews_id.comment',
	'button.label',
	'button.href',
] as const

type GoogleReviewNormalized = {
	author: string
	rating: number
	text: string
	profilePhotoUrl?: string
	time?: number
	relativeTime?: string
	createTime?: string
}

type GoogleReviewsResult = {
	averageRating?: number
	totalReviewCount?: number
	reviews: GoogleReviewNormalized[]
}

const fetcher = async (placesUrl: string): Promise<GoogleReviewsResult> => {
	const bpRes = await fetch('/api/google-business-reviews?all=true')
	if (bpRes.ok) {
		const bp = await bpRes.json()
		return {
			averageRating: bp.averageRating,
			totalReviewCount: bp.totalReviewCount,
			reviews: (bp.reviews || []).map((r: any) => ({
				author: r.author,
				rating: r.rating,
				text: r.text,
				profilePhotoUrl: r.profilePhotoUrl,
				createTime: r.createTime,
			})),
		}
	}

	const res = await fetch(placesUrl, { cache: 'no-store' })
	const contentType = res.headers.get('content-type') || ''
	const data = contentType.includes('application/json')
		? await res.json()
		: { error: await res.text() }

	if (!res.ok) {
		throw new Error(data?.errorMessage || data?.error || `Request failed: ${res.status}`)
	}

	return {
		averageRating: data.rating,
		totalReviewCount: data.userRatingsTotal,
		reviews: (data.reviews || []).map((r: any) => ({
			author: r.author,
			rating: r.rating,
			text: r.text,
			profilePhotoUrl: r.profilePhotoUrl,
			time: r.time,
			relativeTime: r.relativeTime,
		})),
	}
}

export default function HomeReview({ data }: { data: HomeReviewProps }) {
	const [googleQuery, setGoogleQuery] = useState<string>('')

	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const fromUrl = params.get('googleReviewsQuery') || ''
		const fromEnv = process.env.NEXT_PUBLIC_GOOGLE_REVIEWS_QUERY || ''
		setGoogleQuery(fromUrl || fromEnv || 'Harmon Facial Plastic Surgery Cincinnati OH')
	}, [])

	const {
		data: googleData,
		error: googleError,
		isLoading: googleLoading,
	} = useSWR<GoogleReviewsResult>(
		googleQuery ? `/api/google-reviews?query=${encodeURIComponent(googleQuery)}` : null,
		fetcher
	)

	return (
		<section
			className='HomeReview w-full py-8 md:py-24 overflow-hidden relative'
			style={{
				background: 'url(images/home-testimonials-bg.webp) no-repeat',
				backgroundSize: 'cover',
			}}
		>
			<div className='container'>
				{/* Title from Directus */}
				<h2 className='text-4xl md:text-5xl font-agopus text-black mb-8 md:mb-20'>
					{data?.heading?.title || ''}
				</h2>

				{/* Google Reviews Slider */}
				{googleLoading && (
					<p className='text-base font-brandon text-black/70'>Loading reviews…</p>
				)}
				{googleError && (
					<p className='text-base font-brandon text-black/70'>
						Reviews failed to load: {googleError.message}
					</p>
				)}
				{!googleLoading && !googleError && googleData && (
					<GoogleReviewSlider
						reviews={googleData.reviews}
						title={data?.heading?.title || 'Reviews'}
						averageRating={googleData.averageRating}
						totalReviewCount={googleData.totalReviewCount}
						hideTitle
					/>
				)}

				{/* Button from Directus */}
				<div className='relative flex mt-12 max-md:mb-12 justify-center'>
					{data?.button?.label && (
						<Link
							href={data.button?.href || '/'}
							className='btn'
							onClick={e => {
								if (data.button?.href) {
									e.preventDefault()
									window.location.href = data.button.href
								}
							}}
						>
							{data.button.label}
						</Link>
					)}
				</div>
			</div>
		</section>
	)
}
